<template>
    <rkm-modal
        ref="RkmModal"
        title="Encaminhamento"
        extra-large
    >
        <rkm-form
            v-if="model"
            ref="RkmForm"
            show-legend
        >
            <rkm-row>
                <rkm-col>
                    <rkm-form-input-autocomplete
                        :repository="repositories.ocupacao"
                        :search-fields="searchFields"
                        :text-field="(item) => item.codigo ? `${item.codigo} - ${item.nome}` : null"
                        :validations="validations.ocupacao"
                        label="CBO Solicitante"
                        v-model="model.ocupacao"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-multiselect
                        :load-all-automatically="false"
                        :options="data.form.unidades"
                        :select-limit="null"
                        @update:dropdownClosed="handleUnidadesChange"
                        allow-search
                        label="Unidade"
                        option-name-key="nomeFantasia"
                        option-text-selecteds="unidade selecionada"
                        uniform-checkbox
                        v-model="model.unidades"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-multiselect
                        :load-all-automatically="false"
                        :options="data.form.infraestruturas"
                        :select-limit="null"
                        allow-search
                        label="Local de Atendimento"
                        option-name-key="nome"
                        option-text-selecteds="local selecionado"
                        uniform-checkbox
                        v-model="model.infraestruturas"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-multiselect
                        :load-all-automatically="false"
                        :options="data.form.especialidades"
                        :select-limit="null"
                        allow-search
                        label="Especialidades permitidas"
                        option-name-key="descricao"
                        option-text-selecteds="especialidade"
                        uniform-checkbox
                        v-model="model.especialidades"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <template v-slot:footer="{cancel}">
            <rkm-button @click="cancel">
                Fechar
            </rkm-button>

            <rkm-button
                @click="save"
                color="primary"
            >
                {{editingItem ? 'Salvar' : 'Incluir'}}
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
// Mixins.
import {error, object, repository, stringCase} from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        searchFields() {
            return [
                {key: 'ocupacao.codigo', searchable: true},
                {key: 'ocupacao.nome', searchable: true},
            ]
        },
        validations() {
            return {
                ocupacao: {required: true},
            }
        },
    },
    created() {
        this.repositories = {
            ocupacao: this.getConectaSusRepository('sigtap.ocupacao'),
            parametrizacao: this.getConectaSusRepository('ambulatorio.parametrizacao'),
        }
    },
    data: () => ({
        editingItem: null,
        model: {
            ocupacao: null,
            unidades: [],
            infraestruturas: [],
            especialidades: [],
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(item = null) {
            this.resetForm()

            this.editingItem = item

            if (item) {
                this.model.ocupacao = this.clone(item.ocupacao)
                this.model.unidades = this.clone(item.unidades)
                this.model.especialidades = this.clone(item.especialidades)

                this.loadInfraestruturas()
                    .then(() => this.model.infraestruturas = this.clone(item.infraestruturas))
            }
            else {
                this.model.unidades = this.data.form.unidades.map(unidade => unidade.id)

                this.loadInfraestruturas()
                    .then(() => this.model.infraestruturas = this.data.form.infraestruturas.map(infraestrutura => infraestrutura.id))
            }

            this.$refs.RkmModal.show()
        },
        loadInfraestruturas() {
            const request = {
                origem: 'encaminhamento',
                listar: 'infraestruturas',
                unidades: this.model.unidades,
            }

            this.setLoading()

            return this.repositories.parametrizacao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.data.form.infraestruturas = response.data
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUnidadesChange() {
            this.loadInfraestruturas()
                .then(() => this.model.infraestruturas = this.data.form.infraestruturas.map(infraestrutura => infraestrutura.id))
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    if (this.model.unidades.length === 0) {
                        this.showValidationNotification('Selecione ao menos uma unidade.')
                        return
                    }

                    if (this.model.infraestruturas.length === 0) {
                        this.showValidationNotification('Selecione ao menos um local de atendimento.')
                        return
                    }

                    if (this.model.especialidades.length === 0) {
                        this.showValidationNotification('Selecione ao menos uma especialidade permitida.')
                        return
                    }

                    if (this.data.model.encaminhamentos.find(encaminhamento =>
                        encaminhamento !== this.editingItem
                        && encaminhamento.deleted === false
                        && encaminhamento.ocupacao.id === this.model.ocupacao.id
                    )) {
                        this.showValidationNotification('Já existe uma configuração de encaminhamento para este CBO.')
                        return
                    }

                    const unidades = this.data.form.unidades.filter(unidade => this.model.unidades.includes(unidade.id))
                    const infraestruturas = this.data.form.infraestruturas.filter(infraestrutura => this.model.infraestruturas.includes(infraestrutura.id))
                    const especialidades = this.data.form.especialidades.filter(especialidade => this.model.especialidades.includes(especialidade.id))

                    const encaminhamento = {
                        id: this.editingItem ? this.editingItem.id : null,
                        ocupacao: {
                            id: this.model.ocupacao.id,
                            codigo: this.model.ocupacao.codigo,
                            nome: this.model.ocupacao.nome,
                        },
                        unidades: this.model.unidades,
                        unidadesDescricao: unidades.length >= this.data.form.unidades.length
                            ? 'Todas'
                            : `${unidades.length} ${unidades.length === 1 ? 'unidade selecionada' : 'unidades selecionadas'}`,
                        infraestruturas: this.model.infraestruturas,
                        infraestruturasDescricao: infraestruturas.length >= this.data.form.infraestruturas.length
                            ? 'Todos'
                            : `${infraestruturas.length} ${infraestruturas.length === 1 ? 'local selecionado' : 'locais selecionados'}`,
                        especialidades: this.model.especialidades,
                        especialidadesDescricao: especialidades.length >= this.data.form.especialidades.length
                            ? 'Todas'
                            : `${especialidades.length} ${especialidades.length === 1 ? 'especialidade selecionada' : 'especialidades selecionadas'}`,
                        deleted: false,
                    }

                    if (this.editingItem) {
                        Object.assign(this.editingItem, encaminhamento)
                    }
                    else {
                        this.data.model.encaminhamentos.push(encaminhamento)
                    }

                    this.$refs.RkmModal.hide()
                })
        },
        resetForm() {
            this.data.form.infraestruturas = []
            this.editingItem = null
            this.model.ocupacao = null
            this.model.unidades = []
            this.model.infraestruturas = []
            this.model.especialidades = []

            this.$nextTick().then(() => this.$refs.RkmForm.resetValidator())
        },
    },
    mixins: [error, object, repository, stringCase],
    props: {
        data: {required: true, type: Object},
    },
}
</script>
